import axios from "axios";
import React from "react";
import { useState } from "react";
import { useEffect } from "react";

import Plot from "react-plotly.js";
import Box from "@mui/material/Box";

import Typography from "@mui/material/Typography";
import Skeleton from "@mui/material/Skeleton";
import Tooltip from "@mui/material/Tooltip";
import NoWrapTooltip from "../UtilityComponents/TooltipUtilityComponents/NoWrapTooltip";

import AddIcon from "@mui/icons-material/Add";

import StrategyIntro from "./StrategyIntro";
import StrategyDisplayProps from "./StrategyDisplayProps";
import PeriodToggleGroup from "../UtilityComponents/GraphOptionUtilityComponents/PeriodToggleGroup";
import periodToXAxisRange from "../UtilityComponents/GraphOptionUtilityComponents/periodToXAxisRange";
import AsOf from "../UtilityComponents/PaperUtilityComponents/AsOf";

import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";

import LearnTextEmbed from "../LearnPanelComponents/LearnCopy/LearnTextEmbed";

import MyCountUp from "../UtilityComponents/NumberDisplayComponents/MyCountUp";

import { useTheme } from "@mui/material/styles";

const summaryStatPaddingTopBottom = 1;
const enterToolTipDelay = 400;
const leftPadding = 4;

function StrategyDisplay(props: StrategyDisplayProps) {
  const theme = useTheme();
  const strategyPlot = (
    <>
      <Plot
        data={props.plotJson["data"]}
        layout={Object.assign({}, props.plotJson["layout"], {
          width: undefined,
          height: undefined,
          autosize: true,
          font: { ...props.plotJson["layout"]["font"], family: "Roboto", size: 16 },
          xaxis: {
            ...props.plotJson["layout"]["xaxis"],
            range:
              props.historicalPeriod === undefined
                ? undefined
                : periodToXAxisRange(props.historicalPeriod, undefined, props.plotJson["data"]),
            type: "date",
          },
          yaxis: { ...props.plotJson["layout"]["yaxis"], range: [0, props.maxPortfolioValue] },
        })}
        config={Object.assign({}, props.plotJson["config"], {
          displayModeBar: false,
          responsive: true,
        })}
        useResizeHandler={true}
        style={{ width: "100%", height: "100%" }}
        onLegendClick={(event: any) => {
          return false;
        }}
      />
      <AsOf referenceDateString={props.referenceDateString} />
    </>
  );

  return (
    <Box
      sx={{
        overflowY: props.noneSelected ? "auto" : undefined,
        flexGrow: 1,
        display: "flex",
        flexDirection: "column",
      }}
    >
      {props.noneSelected ? (
        <Box
          sx={{
            flexGrow: 0,
            py: summaryStatPaddingTopBottom,
            px: 2,
            sizing: "border-box",
          }}
        >
          <Typography
            variant="h4"
            color="info.main"
            sx={{ position: "sticky", top: 0, background: theme.palette.background.paper }}
          >
            Explore strategy options with historical data
          </Typography>
          <Typography variant="h5" color="text.secondary">
            <LearnTextEmbed content="Learn about popular strategies for managing equity-based compensation below." />
            <br />
            Click any of the <AddIcon />
            <LearnTextEmbed
              content=" to see how your holdings would've grown in the past if our proprietary
            technology had been automatically executing the strategy for you all along. Click added strategies to
            compare historical charts to help you pick a strategy and settings for the future. We'll do the rest!"
            />
          </Typography>
        </Box>
      ) : null}

      {props.noneSelected || !props.plotTitle ? null : (
        <Box sx={{ display: "flex", flexDirection: "row", overflowX: "hidden", justifyContent: "center" }}>
          <Tooltip
            title={"Historical Performance of " + props.plotTitle.trim()}
            placement="top"
            arrow
            enterDelay={enterToolTipDelay}
          >
            <Typography variant="h4" color="text" sx={{ whiteSpace: "nowrap" }}>
              Historical Performance of
              <Typography
                variant="inherit"
                color="info.main"
                sx={{ fontWeight: 500, whiteSpace: "nowrap", display: "inline" }}
              >
                {" " + props.plotTitle.trim()}
              </Typography>
            </Typography>
          </Tooltip>
          {props.plotExtraInfo ? (
            <Box sx={{ alignSelf: "center", pl: 0.5 }}>
              <NoWrapTooltip
                title={<div style={{ whiteSpace: "pre-line" }}>{props.plotExtraInfo}</div>}
                placement="bottom"
                arrow
              >
                <WarningAmberOutlinedIcon color="warning" />
              </NoWrapTooltip>
            </Box>
          ) : null}
        </Box>
      )}
      <Box
        sx={{
          flexGrow: 1,
          width: "100%",
          sizing: "border-box",
          minHeight: 0,
          // overflow: "auto",
        }}
      >
        {props.noneSelected ? (
          props.strategyOptions && (
            <StrategyIntro
              strategyOptions={props.strategyOptions}
              spinAddStrategy={props.spinAddStrategy}
              handleAdd={props.handleAdd}
            />
          )
        ) : props.updated ? (
          strategyPlot
        ) : (
          <Skeleton variant="rectangular" width="100%" height="100%" />
        )}
      </Box>
      {props.noneSelected ? null : (
        <Box
          sx={{
            // height: percentOfPaperHeightForSummaryStats, // TODO: set a min height
            flexGrow: 0,
            py: summaryStatPaddingTopBottom,
            pl: leftPadding,
            display: "flex",
            sizing: "border-box",
            justifyContent: "space-between",
            gap: 2,
            overflowX: "auto",
          }}
        >
          {[
            {
              description: "Total Value",
              value: props.totalValue,
              lastDrawn: props.lastDrawnTotalValue,
              setLastDrawn: props.setLastDrawnTotalValue,
              decimals: 0,
              prepend: "$",
              tooltip_text: "This is the total value under the displayed strategy.",
              display: true,
            },
            {
              description: props.symbol + " Stock Value",
              value: props.coreStockValue,
              lastDrawn: props.lastDrawnCoreStockValue,
              setLastDrawn: props.setLastDrawnCoreStockValue,
              decimals: 0,
              prepend: "$",
              tooltip_text: "This is the value of " + props.symbol + " stock under the displayed strategy.",
              display: true,
            },
            {
              description: props.otherSymbol ? props.otherSymbol + " Stock Value" : "Other Stock Value",
              value: props.otherStockValue,
              lastDrawn: props.lastDrawnOtherStockValue,
              setLastDrawn: props.setLastDrawnOtherStockValue,
              decimals: 0,
              prepend: "$",
              tooltip_text: "This is the value of other stock under the displayed strategy.",
              display: true, //props.otherStockValue > 0,
            },
            {
              description: "Cash Value",
              value: props.cashValue < 0 && props.cashValue > -0.1 ? 0 : props.cashValue, // if negative ten cents make it zero for some rounding error
              lastDrawn: props.lastDrawnCashValue,
              setLastDrawn: props.setLastDrawnCashValue,
              decimals: 0,
              prepend: "$",
              tooltip_text: "This is the cash accumulated under the displayed strategy.",
              display: true,
            },
            // {
            //   description: "Sharpe Ratio",
            //   value: props.sharpeRatio,
            //   decimals: 2,
            //   prepend: "",
            //   tooltip_text: "This is a normalized metric for risk adjusted returns.",
            // },
          ].map(
            (label, index) =>
              label.display && (
                <Tooltip
                  title={label.tooltip_text}
                  placement="bottom-start"
                  arrow
                  key={label.description}
                  enterDelay={enterToolTipDelay}
                >
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "flex-end",
                      sizing: "border-box",
                    }}
                  >
                    <Typography variant="h4" noWrap sx={{}}>
                      {props.noneSelected ? (
                        label.prepend + "-"
                      ) : props.updated ? (
                        <MyCountUp
                          value={label.value}
                          lastFullyDrawnValue={label.lastDrawn}
                          setLastFullyDrawnValue={label.setLastDrawn}
                          prefix={label.prepend}
                          suffix=""
                          decimals={label.decimals}
                          duration={1}
                          negativeIsError
                        />
                      ) : (
                        <Skeleton />
                      )}
                    </Typography>
                    <Typography variant="h6" noWrap sx={{ color: "info.main" }}>
                      {label.description}
                    </Typography>
                  </Box>
                </Tooltip>
              )
          )}
          <Box
            sx={{
              flexGrow: 0,
              sizing: "border-box",
              display: "flex",
              justifyContent: "flex-end",
              alignSelf: "center",
            }}
          >
            <PeriodToggleGroup
              period={props.historicalPeriod || "Max"}
              setPeriod={props.setHistoricalPeriod || ((newHistoricalPeriod) => null)}
            />
          </Box>
        </Box>
      )}
    </Box>
  );
}
export default StrategyDisplay;
